import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, BookOpen, FileText, Download, ExternalLink } from 'lucide-react';
import { getBook } from '../../api/books';

const COVERS = ['#0f2044','#042d1e','#2d1d06','#1a0d2e','#0a1f2e','#2d0a0a'];
const CCOLORS = ['#60a5fa','#34d399','#fbbf24','#a78bfa','#38bdf8','#f87171'];

export default function OpacBookPage() {
  const { id } = useParams();
  const [tab, setTab] = useState<'info'|'read'>('info');

  const { data, isLoading } = useQuery({
    queryKey: ['opac-book', id],
    queryFn: () => getBook(Number(id)),
  } as any);

  if (isLoading) return <div style={{padding:48,textAlign:'center',color:'#475569'}}>იტვირთება...</div>;

  const book = (data as any)?.data;
  if (!book) return <div style={{padding:48,textAlign:'center',color:'#475569'}}>წიგნი ვერ მოიძებნა</div>;

  const ci = (book.id||0) % 6;
  const copies = book.copies || [];
  const available = copies.filter((c: any) => c.status==='available').length;

  return (
    <div style={{paddingTop:24,paddingBottom:48}}>
      <Link to="/opac/catalog" style={{display:'inline-flex',alignItems:'center',gap:6,color:'#475569',textDecoration:'none',fontSize:13,marginBottom:24}}>
        <ArrowLeft size={14}/> კატალოგი
      </Link>

      <div style={{display:'grid',gridTemplateColumns:'auto 1fr',gap:32,marginBottom:32}}>
        <div>
          {book.cover_url ? (
            <img src={book.cover_url} alt={book.title}
              style={{width:160,height:220,objectFit:'cover',borderRadius:12,border:'1px solid #1e2d45',display:'block'}}
              onError={e => {(e.target as any).style.display='none';}} />
          ) : (
            <div style={{width:160,height:220,borderRadius:12,background:COVERS[ci],color:CCOLORS[ci],display:'flex',alignItems:'center',justifyContent:'center',fontSize:56,fontWeight:700,border:`1px solid ${CCOLORS[ci]}33`}}>
              {(book.title||'?')[0]}
            </div>
          )}
        </div>

        <div>
          <h1 style={{fontSize:24,fontWeight:700,color:'#f1f5f9',margin:'0 0 8px',lineHeight:1.3}}>{book.title}</h1>
          <p style={{fontSize:16,color:'#64748b',margin:'0 0 16px'}}>{book.author||'—'}</p>

          <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:20}}>
            <span style={{padding:'5px 12px',borderRadius:20,fontSize:13,fontWeight:500,
              background:available>0?'#042d1e':'#2d0a0a',
              color:available>0?'#34d399':'#f87171',
              border:`1px solid ${available>0?'#0a4a2a':'#5a1a1a'}`}}>
              {available>0?`✓ ${available} ხელმისაწვდომი`:'✗ გაცემულია'}
            </span>
            {book.collection_name && (
              <span style={{padding:'5px 12px',borderRadius:20,fontSize:13,background:'#0f2044',color:'#60a5fa',border:'1px solid #1e3a6e'}}>{book.collection_name}</span>
            )}
            {book.language && (
              <span style={{padding:'5px 12px',borderRadius:20,fontSize:13,background:'#111827',color:'#64748b',border:'1px solid #1e2d45'}}>{book.language==='ka'?'ქართული':book.language}</span>
            )}
            {book.file_path && book.file_type==='pdf' && (
              <span style={{padding:'5px 12px',borderRadius:20,fontSize:13,background:'#1a0d2e',color:'#a78bfa',border:'1px solid #3a1d6e'}}>📄 PDF ხელმისაწვდომია</span>
            )}
          </div>

          <div style={{display:'grid',gridTemplateColumns:'repeat(3,auto)',gap:12,marginBottom:20}}>
            {[
              {label:'გამომცემელი', val:book.publisher||'—'},
              {label:'წელი', val:book.pub_year||'—'},
              {label:'ISBN', val:book.isbn||'—'},
            ].map(f => (
              <div key={f.label} style={{background:'#0d1424',border:'1px solid #1e2d45',borderRadius:8,padding:'10px 14px'}}>
                <div style={{fontSize:10,color:'#475569',marginBottom:3}}>{f.label}</div>
                <div style={{fontSize:13,color:'#cbd5e1',fontWeight:500}}>{f.val}</div>
              </div>
            ))}
          </div>

          <div style={{display:'flex',gap:10}}>
            {book.file_path && book.file_type==='pdf' && (
              <button onClick={() => setTab('read')}
                style={{display:'flex',alignItems:'center',gap:6,padding:'10px 20px',background:'#1d4ed8',border:'none',borderRadius:8,color:'white',fontSize:14,fontWeight:500,cursor:'pointer'}}>
                <BookOpen size={16}/> ონლაინ კითხვა
              </button>
            )}
            {book.file_path && (
              <a href={`/uploads/${book.file_path}`} target="_blank" rel="noreferrer"
                style={{display:'flex',alignItems:'center',gap:6,padding:'10px 20px',background:'#1a0d2e',border:'1px solid #3a1d6e',borderRadius:8,color:'#a78bfa',fontSize:14,textDecoration:'none'}}>
                <ExternalLink size={16}/> გახსნა
              </a>
            )}
          </div>
        </div>
      </div>

      <div style={{background:'#0d1424',border:'1px solid #1e2d45',borderRadius:12,overflow:'hidden'}}>
        <div style={{display:'flex',borderBottom:'1px solid #1e2d45'}}>
          {[
            {key:'info', label:'📋 ინფო'},
            ...(book.file_path && book.file_type==='pdf' ? [{key:'read', label:'📖 კითხვა'}] : []),
          ].map(t => (
            <button key={t.key} onClick={() => setTab(t.key as any)}
              style={{padding:'12px 20px',background:'none',border:'none',borderBottom:tab===t.key?'2px solid #60a5fa':'2px solid transparent',marginBottom:-1,fontSize:13,color:tab===t.key?'#60a5fa':'#475569',cursor:'pointer',fontWeight:tab===t.key?500:400}}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'info' && (
          <div style={{padding:20}}>
            {book.description
              ? <p style={{fontSize:14,color:'#94a3b8',lineHeight:1.8}}>{book.description}</p>
              : <p style={{fontSize:14,color:'#334155',fontStyle:'italic'}}>აღწერა არ არის</p>}

            {copies.length > 0 && (
              <div style={{marginTop:20}}>
                <h3 style={{fontSize:14,fontWeight:500,color:'#cbd5e1',marginBottom:12}}>ასლები ({copies.length})</h3>
                {copies.slice(0,5).map((c: any) => (
                  <div key={c.id} style={{display:'flex',alignItems:'center',gap:10,padding:'8px 0',borderBottom:'1px solid #0f1826'}}>
                    <div style={{width:8,height:8,borderRadius:'50%',background:c.status==='available'?'#34d399':'#fbbf24',flexShrink:0}} />
                    <span style={{fontSize:12,color:'#475569',fontFamily:'monospace',flex:1}}>{c.barcode||`#${c.id}`}</span>
                    <span style={{fontSize:12,fontWeight:500,color:c.status==='available'?'#34d399':'#fbbf24'}}>
                      {c.status==='available'?'ხელმისაწვდომი':'გაცემული'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === 'read' && book.file_path && book.file_type==='pdf' && (
          <div style={{height:'80vh'}}>
            <iframe src={`/uploads/${book.file_path}`} width="100%" height="100%"
              style={{border:'none',display:'block'}} title={book.title} />
          </div>
        )}
      </div>
    </div>
  );
}
